/**
 * The credential profile an app sends with (D-44): which profile, its credentials (read-only here),
 * and a switch that shows what changes before it happens.
 */
import { type App, type CredentialProfileDetail, isPushChannel } from "@aiyo/shared";
import { ArrowRightArrowLeft, Pencil } from "@gravity-ui/icons";
import { Button, Chip, toast } from "@heroui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { FormDialog } from "../../components/dialogs";
import { SelectInput } from "../../components/fields";
import { ProfileEntries } from "../../components/ProfileEntries";
import { Callout, ErrorBlock, LoadingBlock, Panel } from "../../components/ui";
import { useProfile, useProfiles, useUpdateApp } from "../../lib/queries";

export function ProfileTab({ app, canEdit }: { app: App; canEdit: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const profile = useProfile(app.credential_profile.id);
  const [switching, setSwitching] = useState(false);

  if (profile.isPending) return <LoadingBlock />;
  if (profile.isError) return <ErrorBlock error={profile.error} onRetry={() => profile.refetch()} />;
  const p = profile.data;
  const others = p.apps.filter((a) => a.id !== app.id);

  return (
    <div className="flex flex-col gap-6">
      <Panel
        title={
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-normal text-muted">{t("profiles.app.current")}</span>
            <Link to={`/credential-profiles/${p.id}`} className="hover:underline">
              {p.name}
            </Link>
          </span>
        }
        description={p.description ?? undefined}
        actions={
          <>
            <Button variant="secondary" onPress={() => navigate(`/credential-profiles/${p.id}`)}>
              <Pencil />
              {t("profiles.app.edit")}
            </Button>
            {canEdit ? (
              <Button onPress={() => setSwitching(true)}>
                <ArrowRightArrowLeft />
                {t("profiles.app.change")}
              </Button>
            ) : null}
          </>
        }
      >
        {others.length ? (
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted">
            {t("profiles.usedBy")}
            {p.apps.map((a) => (
              <Chip
                key={a.id}
                size="sm"
                variant={a.id === app.id ? "soft" : "secondary"}
                color={a.id === app.id ? "accent" : "default"}
              >
                {a.name}
              </Chip>
            ))}
          </div>
        ) : null}
        <p className="text-sm text-muted">{t("profiles.app.readOnly")}</p>
        <ProfileEntries profile={p} canEdit={false} />
      </Panel>

      {canEdit ? (
        <SwitchDialog app={app} current={p} isOpen={switching} onClose={() => setSwitching(false)} />
      ) : null}
    </div>
  );
}

/** What switching from one profile to another would change, for the warning list. */
export function compareProfiles(current: CredentialProfileDetail, target: CredentialProfileDetail) {
  const targetAliases = new Set(target.entries.map((e) => e.alias));
  const targetChannels = new Set(target.entries.map((e) => e.channel));
  const isMock = (code: string) => code.startsWith("mock-");
  return {
    missingAliases: [...new Set(current.entries.map((e) => e.alias))].filter((a) => !targetAliases.has(a)),
    missingChannels: [...new Set(current.entries.map((e) => e.channel))].filter(
      (c) => !targetChannels.has(c),
    ),
    // A push token belongs to one Firebase project: moving between two real push credentials can break tokens.
    pushChanged: current.entries
      .filter((e) => isPushChannel(e.channel) && !isMock(e.credential.provider.code))
      .filter((e) => {
        const next = target.entries.find((x) => x.alias === e.alias);
        return next && next.credential.id !== e.credential.id && !isMock(next.credential.provider.code);
      })
      .map((e) => e.alias),
  };
}

function SwitchDialog({
  app,
  current,
  isOpen,
  onClose,
}: {
  app: App;
  current: CredentialProfileDetail;
  isOpen: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const profiles = useProfiles();
  const update = useUpdateApp(app.id);
  const [targetId, setTargetId] = useState<string | null>(null);
  const target = useProfile(targetId ?? undefined);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when the dialog opens.
  useEffect(() => {
    if (isOpen) {
      setTargetId(null);
      update.reset();
    }
  }, [isOpen]);

  const diff = target.data ? compareProfiles(current, target.data) : null;
  const warnings = diff
    ? [
        diff.missingAliases.length
          ? t("profiles.app.missingAliases", { aliases: diff.missingAliases.join(", ") })
          : null,
        diff.missingChannels.length
          ? t("profiles.app.missingChannels", {
              channels: diff.missingChannels.map((c) => t(`channels.${c}`)).join(", "),
            })
          : null,
        diff.pushChanged.length
          ? t("profiles.app.pushChanged", { aliases: diff.pushChanged.join(", ") })
          : null,
      ].filter((w): w is string => Boolean(w))
    : [];

  return (
    <FormDialog
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      title={t("profiles.app.changeTitle")}
      description={t("profiles.app.changeHint")}
      submitLabel={t("profiles.app.change")}
      isPending={update.isPending}
      isSubmitDisabled={!targetId || !target.data}
      error={update.error}
      onSubmit={() =>
        targetId &&
        update.mutate(
          { credential_profile_id: targetId },
          {
            onSuccess: () => {
              toast.success(t("profiles.app.switched"));
              onClose();
            },
          },
        )
      }
    >
      <SelectInput
        label={t("profiles.app.target")}
        placeholder={t("profiles.app.target")}
        value={targetId}
        onChange={setTargetId}
        isRequired
        options={(profiles.data ?? [])
          .filter((p) => p.id !== current.id)
          .map((p) => ({
            id: p.id,
            label: p.name,
            hint:
              p.channels.map((c) => `${t(`channels.${c.channel}`)}: ${c.aliases.join(", ")}`).join(" · ") ||
              undefined,
          }))}
      />
      {target.isFetching && !target.data ? <LoadingBlock /> : null}
      {diff ? (
        warnings.length ? (
          <Callout tone="warning">
            <ul className="flex flex-col gap-1.5">
              {warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </Callout>
        ) : (
          <Callout tone="success">{t("profiles.app.same")}</Callout>
        )
      ) : null}
    </FormDialog>
  );
}
